import {
  faCheckCircle,
  faExclamationCircle,
  faTools,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { FC, useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { getTicketStatistics } from 'src/store/actions/ticketActions';
import { TicketStatus } from 'src/store/models/Ticket';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import { DateRange } from '../../../../store/models/Stats';
import useIntersectionObserver from '../../../../utils/useIntersection';
import ShadowBox from '../../ShadowBox/ShadowBox';
import classes from './StatisticsItem.module.scss';
import StatisticsItemSkeleton from './StatisticsItemSkeleton';

interface StatisticsItemProps {
  status: TicketStatus;
  range: DateRange;
  title: string;
  className?: string;
}

const StatisticsItem: FC<StatisticsItemProps> = ({
  status,
  className,
  range,
  title,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const startRef = useRef(null);

  const entry = useIntersectionObserver(ref, { freezeOnceVisible: true });
  const isVisible = !!entry?.isIntersecting;
  const loading = useLoadingSelector({
    actionType: getTicketStatistics.type,
    id: status,
  });
  const statistics = useAppSelector((state) => state.ticket.statistics);
  const currStats = statistics?.[status];

  useEffect(() => {
    dispatch(getTicketStatistics({ range, status }));
    // eslint-disable-next-line
  }, [range]);

  const before = currStats?.before?.[0]?._count?.status;
  const current = currStats?.current?.[0]?._count?.status;

  useEffect(() => {
    if (isVisible && !loading) {
      startRef?.current?.();
    }
  }, [isVisible, loading]);

  const getIcon = (): JSX.Element => {
    switch (status) {
      case TicketStatus.UNASSIGNED:
        return (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className={classes.Icon}
          />
        );
      case TicketStatus.RESOLVED:
        return (
          <FontAwesomeIcon icon={faCheckCircle} className={classes.Icon} />
        );
      case TicketStatus.IN_PROGRESS:
        return <FontAwesomeIcon icon={faTools} className={classes.Icon} />;
      default:
        return null;
    }
  };

  const getText = () => {
    if (status === TicketStatus.IN_PROGRESS) {
      const toDoCurrent =
        statistics?.[TicketStatus.TO_DO]?.current?.[0]?._count?.status;
      const unassignedCurrent =
        statistics?.[TicketStatus.UNASSIGNED]?.current?.[0]?._count?.status;
      const inProgressCurrent =
        statistics?.[TicketStatus.IN_PROGRESS]?.current?.[0]?._count?.status;
      const percent =
        inProgressCurrent /
        (inProgressCurrent + unassignedCurrent + toDoCurrent);
      return t(`dashboard:of_all_unresolved`, {
        percent: (percent || 0).toFixed(2),
      });
    }
    if (before === undefined || current === undefined) {
      return t('dashboard:no_data');
    }
    const diff = parseInt(((current / before - 1) * 100).toFixed(0));
    return t(`dashboard:compared_to_${range}`, {
      diff: `${diff >= 0 ? `+${diff}` : diff}`,
    });
  };

  const positive =
    status !== TicketStatus.IN_PROGRESS &&
    (status === TicketStatus.UNASSIGNED ? current < before : current > before);

  return (
    <ShadowBox
      className={classNames(classes.Container, className, {
        [classes.Loading]: loading,
      })}
    >
      {!loading ? (
        <>
          {getIcon()}
          <p className={classes.Title}>{title}</p>
          <div className={classes.Counter} ref={ref}>
            <CountUp end={current} duration={1} start={0}>
              {({ countUpRef, start }) => {
                startRef.current = start;
                return <span ref={countUpRef} />;
              }}
            </CountUp>
          </div>
          <p
            className={classNames({
              [classes.Positive]: positive,
              [classes.Negative]: !positive,
              [classes.Neutral]:
                status === TicketStatus.IN_PROGRESS ||
                current === before ||
                current === undefined ||
                before === undefined,
            })}
          >
            {getText()}
          </p>
        </>
      ) : (
        <StatisticsItemSkeleton />
      )}
    </ShadowBox>
  );
};

export default StatisticsItem;
